import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingSmall,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@khan/data-gql";
import {reload} from "@khan/urls";

import type {passwordChangeMutation_passwordChange as PasswordChange} from "./__graphql-types__/passwordChangeMutation.ts";
import changePasswordErrorMessage from "./change-password-error-message.ts";
import CHANGE_PASSWORD_MUTATION from "./change-password-mutation.ts";
import RequireReauth from "./require-reauth.tsx";

interface Props {
    actorIsChild: boolean;
    hasPassword: boolean;
    // If the user is a parent modifying their child's settings.
    // Please note that the child may be over 13.
    isModifyingChild: boolean;
    requiresExistingPassword: boolean;
    targetId: string;
    actorIdentifier: string | null | undefined;
    successfullyChangedSettings: () => unknown;
    isClasslinkUser: boolean;
}

const PasswordForm = ({
    actorIsChild,
    hasPassword,
    isModifyingChild,
    requiresExistingPassword,
    targetId,
    actorIdentifier,
    successfullyChangedSettings,
    isClasslinkUser,
}: Props): React.ReactElement => {
    const [existing, setExisting] = useState<string | null | undefined>(null);
    const [password1, setPassword1] = useState<string | null | undefined>(null);
    const [password2, setPassword2] = useState<string | null | undefined>(null);
    const [password2Error, setPassword2Error] = useState<
        string | null | undefined
    >(null);

    const isMountedRef = useRef(true);
    const [changePasswordMutation, result] = useMutation(
        CHANGE_PASSWORD_MUTATION,
    );

    useEffect(() => {
        // Component mounted
        return () => {
            // Component will unmount
            isMountedRef.current = false;
        };
    }, []);

    const validateExisting = useCallback(
        (value: string): string | null | undefined => {
            if (existing === null) {
                return;
            }
            if (requiresExistingPassword && !value) {
                return t`Please enter your existing password`;
            }
        },
        [existing, requiresExistingPassword],
    );

    const validatePassword1 = useCallback(
        (value: string): string | null | undefined => {
            if (password1 === null) {
                return;
            }
            if (!value) {
                return t`Password cannot be blank`;
            }
        },
        [password1],
    );

    const handlePasswordBlur = useCallback((): void => {
        if (password1 && password2) {
            const match = password1 === password2;
            setPassword2Error(!match ? t`Passwords don't match` : null);
        }
    }, [password1, password2]);

    const getError = useCallback(
        (
            serverError?: Error | null | undefined,
            error?: PasswordChange["error"],
            password2Error?: string | null | undefined,
        ): string | null | undefined => {
            if (serverError) {
                return t`Oops. Something went wrong. Please try again.`;
            }
            if (error) {
                return changePasswordErrorMessage(error.code);
            }
            return password2Error;
        },
        [],
    );

    const processPasswordChangeResult = ({
        error,
        serverError,
    }: {
        error: PasswordChange["error"];
        serverError: Error | null | undefined;
    }): void => {
        // Don't do anything if there was an error when saving
        if (error || serverError) {
            return;
        }

        successfullyChangedSettings();
        setExisting("");
        setPassword1("");
        setPassword2("");

        if (!isModifyingChild && !requiresExistingPassword) {
            reload();
        }
    };

    const renderSubmitButton = useCallback(
        (isLoading: boolean, onChange: () => unknown): React.ReactNode => {
            const invalidForm =
                (requiresExistingPassword && !existing) ||
                !password1 ||
                password1 !== password2;
            // We only require reauthentication on setting a password, as changing
            // your password requires you to input your existing password
            const submitButton = (label: string, onClick: () => unknown) => {
                return (
                    <Button
                        onClick={onClick}
                        style={styles.button}
                        disabled={invalidForm}
                        spinner={isLoading}
                        testId="password-form-submit"
                    >
                        {label}
                    </Button>
                );
            };

            if (hasPassword || isModifyingChild) {
                return submitButton(t`Change password`, onChange);
            }

            return (
                <RequireReauth
                    identifier={actorIdentifier}
                    showClasslinkAuthProvider={isClasslinkUser}
                    onSuccess={onChange}
                    onFailure={() => {}}
                    hasPasswordField={false}
                >
                    {(invokeReauth) =>
                        submitButton(t`Create password`, invokeReauth)
                    }
                </RequireReauth>
            );
        },
        [
            actorIdentifier,
            hasPassword,
            isClasslinkUser,
            isModifyingChild,
            requiresExistingPassword,
            existing,
            password1,
            password2,
        ],
    );

    if (actorIsChild) {
        return (
            <View style={{gap: sizing.size_160}}>
                <HeadingSmall style={styles.header} tag="h2">
                    {t`Password`}
                </HeadingSmall>
                <Body>
                    {t`Your parent must log in to change your password.`}
                </Body>
            </View>
        );
    }

    const isLoading = result?.status === "loading";
    const serverError = result?.status === "error" ? result.error : null;
    const error =
        result?.status === "success" ? result.data.passwordChange?.error : null;

    const finalErrorMessage = getError(serverError, error, password2Error);

    return (
        <View
            style={{gap: sizing.size_160}}
            role="region"
            aria-label={t`Password settings`}
        >
            <HeadingSmall style={styles.header} tag="h2">
                {t`Password`}
            </HeadingSmall>
            <View>
                {requiresExistingPassword && (
                    <View style={{marginBlockEnd: sizing.size_160}}>
                        <LabeledField
                            field={
                                <TextField
                                    id="existing"
                                    type="password"
                                    value={existing || ""}
                                    onChange={(newValue) =>
                                        setExisting(newValue)
                                    }
                                    validate={validateExisting}
                                />
                            }
                            label={t`Current password`}
                        />
                    </View>
                )}
                {!hasPassword && (
                    <Body style={styles.textSpacing}>
                        {t`Creating a password allows you to log in with your Khan Academy username and password.`}
                    </Body>
                )}
                <View style={{marginBlockEnd: sizing.size_160}}>
                    <LabeledField
                        field={
                            <TextField
                                testId="password1"
                                id="password1"
                                type="password"
                                value={password1 || ""}
                                onChange={(newValue) => setPassword1(newValue)}
                                validate={validatePassword1}
                                onBlur={handlePasswordBlur}
                            />
                        }
                        label={t`New password`}
                    />
                </View>
                <View style={{marginBlockEnd: sizing.size_160}}>
                    <LabeledField
                        field={
                            <TextField
                                testId="password2"
                                id="password2"
                                type="password"
                                value={password2 || ""}
                                onChange={(newValue) => setPassword2(newValue)}
                                onBlur={handlePasswordBlur}
                            />
                        }
                        label={t`Re-enter new password`}
                    />
                    <View aria-live="polite" data-testid="password-error">
                        {finalErrorMessage && (
                            <LabelSmall style={styles.errorMessage}>
                                {finalErrorMessage}
                            </LabelSmall>
                        )}
                    </View>
                </View>
                {renderSubmitButton(isLoading, async () => {
                    const result = await changePasswordMutation({
                        kaid: targetId,
                        password: password1 || "",
                        oldPassword: existing || "",
                    });
                    const data =
                        result.status === "success" ? result.data : null;
                    processPasswordChangeResult({
                        error: data?.passwordChange?.error,
                        serverError: null,
                    });
                })}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    header: {
        color: semanticColor.core.foreground.neutral.strong,
    },
    button: {
        alignSelf: "flex-start",
        paddingInlineStart: sizing.size_160,
        paddingInlineEnd: sizing.size_160,
    },
    errorMessage: {
        color: semanticColor.status.critical.foreground,
        marginBlockStart: sizing.size_120,
    },
    textSpacing: {
        paddingBlockEnd: sizing.size_120,
    },
});

export default PasswordForm;
